import { useCallback, useState, useRef } from 'react'
import classNames from 'classnames'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form/dist/index.ie11'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faCheck } from '@fortawesome/pro-regular-svg-icons'
import formatError from '../../../utilities/formatError'
import { formatPercentage } from '../utils'
import { Stack } from '../../stack'
import { Button } from '../../button'
import { TextField } from '../../textfield'
import { Alert } from '../../alert'
import { Row, Col } from '../../grid'
import { Heading } from '../../typography/Heading'
import computeOffsetTop from '../../../utilities/computeTopOffset'
import useEnabledOnClient from '../../../utilities/useEnabledOnClient'
import * as styles from '../CalculatorModule.module.scss'

const validationSchema = {
  interestRate: {
    min: 0,
    max: 40,
  },
  termYears: {
    min: 0,
    max: 40,
  },
  cc1Balance: {
    min: 0,
    max: 999999999,
  },
  cc1Rate: {
    min: 0,
    max: 40,
  },
  cc2Balance: {
    min: 0,
    max: 999999999,
  },
  cc2Rate: {
    min: 0,
    max: 40,
  },
  cc3Balance: {
    min: 0,
    max: 999999999,
  },
  cc3Rate: {
    min: 0,
    max: 40,
  },
  cc4Balance: {
    min: 0,
    max: 999999999,
  },
  cc4Rate: {
    min: 0,
    max: 40,
  },
  auto1Balance: {
    min: 0,
    max: 999999999,
  },
  auto1Rate: {
    min: 0,
    max: 40,
  },
  auto2Balance: {
    min: 0,
    max: 999999999,
  },
  auto2Rate: {
    min: 0,
    max: 40,
  },
  rvBalance: {
    min: 0,
    max: 999999999,
  },
  rvRate: {
    min: 0,
    max: 40,
  },
  other1Balance: {
    min: 0,
    max: 999999999,
  },
  other1Rate: {
    min: 0,
    max: 40,
  },
  other2Balance: {
    min: 0,
    max: 999999999,
  },
  other2Rate: {
    min: 0,
    max: 40,
  },
  other3Balance: {
    min: 0,
    max: 999999999,
  },
  other3Rate: {
    min: 0,
    max: 40,
  },
}

const PFDebtConsolidationCalculator = () => {
  const [t, i18n] = useTranslation()
  const [language, countryCode] = i18n.language.split('-')
  const [calcResults, setCalcResults] = useState()
  // Our API doesn’t support sync form submissions and so we need Javascript to submit the form asynchronously by issuing an XHR. Therefore, we need to ensure that the button is initially disabled and gets re-enabled once all Javascript has loaded on the client.
  const disabled = useEnabledOnClient()

  const creditCardLabel = t('PFDebtConsolidationCalculator.creditCard', {
    defaultValue: 'Credit card',
  })
  const autoLoanLabel = t('PFDebtConsolidationCalculator.autoLoan', {
    defaultValue: 'Auto loan',
  })
  const otherLoanLabel = t('PFDebtConsolidationCalculator.otherLoan', {
    defaultValue: 'Other loan',
  })
  const fieldGroups = [
    {
      label: `${creditCardLabel} 1 ($)`,
      fields: ['cc1Balance', 'cc1Payment', 'cc1Rate'],
    },
    {
      label: `${creditCardLabel} 2 ($)`,
      fields: ['cc2Balance', 'cc2Payment', 'cc2Rate'],
    },
    {
      label: `${creditCardLabel} 3 ($)`,
      fields: ['cc3Balance', 'cc3Payment', 'cc3Rate'],
    },
    {
      label: `${creditCardLabel} 4 ($)`,
      fields: ['cc4Balance', 'cc4Payment', 'cc4Rate'],
    },
    {
      label: `${autoLoanLabel} 1 ($)`,
      fields: ['auto1Balance', 'auto1Payment', 'auto1Rate'],
    },
    {
      label: `${autoLoanLabel} 2 ($)`,
      fields: ['auto2Balance', 'auto2Payment', 'auto2Rate'],
    },
    {
      label: t('PFDebtConsolidationCalculator.boatRVLoan', {
        defaultValue: 'Boat/RV loan ($)',
      }),
      fields: ['rvBalance', 'rvPayment', 'rvRate'],
    },
    {
      label: `${otherLoanLabel} 1 ($)`,
      fields: ['other1Balance', 'other1Payment', 'other1Rate'],
    },
    {
      label: `${otherLoanLabel} 2 ($)`,
      fields: ['other2Balance', 'other2Payment', 'other2Rate'],
    },
    {
      label: `${otherLoanLabel} 3 ($)`,
      fields: ['other3Balance', 'other3Payment', 'other3Rate'],
    },
  ]
  const { register, errors, handleSubmit } = useForm({
    mode: 'onBlur',
    defaultValues: {
      interestRate: 6,
      termYears: 15,
      cc1Balance: 0,
      cc1Payment: 0,
      cc1Rate: 0,
      cc2Balance: 0,
      cc2Payment: 0,
      cc2Rate: 0,
      cc3Balance: 0,
      cc3Payment: 0,
      cc3Rate: 0,
      cc4Balance: 0,
      cc4Payment: 0,
      cc4Rate: 0,
      auto1Balance: 0,
      auto1Payment: 0,
      auto1Rate: 0,
      auto2Balance: 0,
      auto2Payment: 0,
      auto2Rate: 0,
      rvBalance: 0,
      rvPayment: 0,
      rvRate: 0,
      other1Balance: 0,
      other1Payment: 0,
      other1Rate: 0,
      other2Balance: 0,
      other2Payment: 0,
      other2Rate: 0,
      other3Balance: 0,
      other3Payment: 0,
      other3Rate: 0,
    },
  })

  const [inProgress, setInProgress] = useState(false)
  const [alert, showAlert] = useState(null)
  const dismissAlert = useCallback(() => showAlert(null), [])
  const resultsContainer = useRef(null)
  const resultsHeader = useRef(null)

  const handleSubmitValid = async formData => {
    setInProgress(true)
    showAlert(false)

    try {
      const url = 'https://www.calcxml.com/rest/det06'

      // Documentation
      // https://www.calcxml.com/documentation/rest/Det06JSONRequest.htm

      const payload = {
        username: process.env.CALCXML_USERNAME,
        password: process.env.CALCXML_PASSWORD,
        language,
        countryCode,
        chartWidth: 900,
        chartHeight: 400,
        barColor1: '63B5E4',
        barColor2: '84BD00',
        barColor3: 'FCC841',
        // barColor4: '#ffffff',
        // barColor5: '#ffffff',
        // barColor6: '#ffffff',
        interestRate: formatPercentage(formData.interestRate),
        termYears: formData.termYears,
        cc1Balance: formData.cc1Balance,
        cc1Payment: formData.cc1Payment,
        cc1Rate: formatPercentage(formData.cc1Rate),
        cc2Balance: formData.cc2Balance,
        cc2Payment: formData.cc2Payment,
        cc2Rate: formatPercentage(formData.cc2Rate),
        cc3Balance: formData.cc3Balance,
        cc3Payment: formData.cc3Payment,
        cc3Rate: formatPercentage(formData.cc3Rate),
        cc4Balance: formData.cc4Balance,
        cc4Payment: formData.cc4Payment,
        cc4Rate: formatPercentage(formData.cc4Rate),
        auto1Balance: formData.auto1Balance,
        auto1Payment: formData.auto1Payment,
        auto1Rate: formatPercentage(formData.auto1Rate),
        auto2Balance: formData.auto2Balance,
        auto2Payment: formData.auto2Payment,
        auto2Rate: formatPercentage(formData.auto2Rate),
        rvBalance: formData.rvBalance,
        rvPayment: formData.rvPayment,
        rvRate: formatPercentage(formData.rvRate),
        other1Balance: formData.other1Balance,
        other1Payment: formData.other1Payment,
        other1Rate: formatPercentage(formData.other1Rate),
        other2Balance: formData.other2Balance,
        other2Payment: formData.other2Payment,
        other2Rate: formatPercentage(formData.other2Rate),
        other3Balance: formData.other3Balance,
        other3Payment: formData.other3Payment,
        other3Rate: formatPercentage(formData.other3Rate),
      }

      const response = await axios.post(url, payload)

      if (response.status !== 200 || Boolean(response.data.error)) {
        throw new Error(`Unexpected status code: ${response.status} ${response.statusText}`)
      }

      setInProgress(false)
      setCalcResults(response.data)
      window.scrollTo({
        top: computeOffsetTop(resultsContainer.current) - 20,
        behavior: 'smooth',
      })
      resultsHeader.current.focus()
    } catch (error) {
      setInProgress(false)
      showAlert({
        type: 'error',
        title: t('PFDebtConsolidationCalculator.error', {
          defaultValue: 'Error',
        }),
        message: error.message,
      })
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  return (
    <div>
      {alert && (
        <Row>
          <Col xs={12}>
            <Alert variant={alert.type} title={alert.title} onDismiss={dismissAlert}>
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}
      <form
        action="/"
        method="POST"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleSubmitValid)}
        className={alert ? styles.HasAlert : ''}
      >
        <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
          <legend className={styles.Legend}>
            {t('PFDebtConsolidationCalculator.firstLegend', {
              defaultValue: 'Consolidated loan information',
            })}
          </legend>
          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('PFDebtConsolidationCalculator.interestRateLabel', {
                  defaultValue: 'Annual interest rate (%)',
                })}
                id="interestRate"
                name="interestRate"
                type="number"
                min="0"
                max="40"
                ref={register(validationSchema.interestRate)}
                error={formatError(errors.interestRate, validationSchema.interestRate, t)}
                autoFocus
                autoSelect
              />
            </Col>

            <Col xs={12} lg={6}>
              <TextField
                label={t('PFDebtConsolidationCalculator.termYearsLabel', {
                  defaultValue: 'Number of years (1 to 40)',
                })}
                id="termYears"
                name="termYears"
                type="number"
                min="1"
                max="40"
                ref={register(validationSchema.termYears)}
                error={formatError(errors.termYears, validationSchema.termYears, t)}
                autoSelect
              />
            </Col>
          </Row>
        </fieldset>

        <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
          <legend className={styles.Legend}>
            {t('PFDebtConsolidationCalculator.secondLegend', {
              defaultValue: 'Current debt information',
            })}
          </legend>
          <Row>
            <Col xs={12}>
              <table className={styles.ResultsTable} role="grid">
                <caption>
                  {t('PFDebtConsolidationCalculator.tableCaption', {
                    defaultValue: 'Current debt information table',
                  })}
                </caption>
                <tbody>
                  <tr role="row">
                    <th scope="col" aria-label="Empty" />
                    <th scope="col">
                      {t('PFDebtConsolidationCalculator.balance', {
                        defaultValue: 'Balance',
                      })}
                    </th>
                    <th scope="col" className={styles.ResultsTableCenter}>
                      {t('PFDebtConsolidationCalculator.monthlyPayment', {
                        defaultValue: 'Monthly payment',
                      })}
                    </th>
                    <th scope="col">
                      {t('PFDebtConsolidationCalculator.yearlyRate', {
                        defaultValue: 'Yearly rate (0% to 40%)',
                      })}
                    </th>
                  </tr>
                  {fieldGroups.map(group => (
                    <tr role="row">
                      <td>{group.label}</td>
                      {group.fields.map(field => {
                        const fieldName = field
                        return (
                          <td>
                            <TextField
                              label={field}
                              id={field}
                              name={field}
                              type="number"
                              min="0"
                              ref={register(validationSchema[fieldName])}
                              visuallyHideLabel
                              error={formatError(errors[fieldName], validationSchema[fieldName], t)}
                              autoSelect
                            />
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Col>
          </Row>
        </fieldset>

        <Stack alignment="center" className={styles.Submit}>
          <Button
            type="submit"
            disabled={inProgress || disabled}
            loading={inProgress}
            alt={t('PFDebtConsolidationCalculator.calculateAlt', {
              defaultValue: 'Calculate to view results',
            })}
          >
            {t('PFDebtConsolidationCalculator.calculate', {
              defaultValue: 'Calculate',
            })}
          </Button>

          {alert && alert.type === 'error' && (
            <span className={styles.ShortMessage}>
              <FontAwesomeIcon icon={faTimes} className={styles.TimesIcon} />
              {t('PFDebtConsolidationCalculator.couldNotCalculate', {
                defaultValue: 'Sorry, could not calculate',
              })}
            </span>
          )}

          {alert && alert.type === 'success' && (
            <span className={styles.ShortMessage}>
              <FontAwesomeIcon icon={faCheck} className={styles.CheckIcon} />
              {t('PFDebtConsolidationCalculator.calculationComplete', {
                defaultValue: 'Calculation complete. See below for results!',
              })}
            </span>
          )}
        </Stack>
      </form>

      <div ref={resultsContainer} />

      {calcResults && (
        <div className={styles.Results}>
          <Heading
            className={styles.ResultsHeading}
            element="h3"
            size={3}
            tabIndex="0"
            forwardedRef={resultsHeader}
          >
            {t('PFDebtConsolidationCalculator.results', {
              defaultValue: 'Results',
            })}
          </Heading>
          <p>{calcResults.responseText}</p>

          <Heading className={styles.ResultsHeading} element="h3" size={3}>
            {t('PFDebtConsolidationCalculator.summaryTable', {
              defaultValue: 'Summary table',
            })}
          </Heading>

          <table className={styles.ResultsTable} role="grid">
            <caption>
              {t('PFDebtConsolidationCalculator.summaryTable', {
                defaultValue: 'Summary table',
              })}
            </caption>
            <tbody>
              <tr role="row">
                <th className="section">
                  {t('PFDebtConsolidationCalculator.currentDebtAnalysis', {
                    defaultValue: 'Current debt analysis',
                  })}
                </th>
                <th className="hiddenHeader">
                  {t('PFDebtConsolidationCalculator.value', {
                    defaultValue: 'Value',
                  })}
                </th>
              </tr>
              <tr role="row">
                <th scope="row">
                  {t('PFDebtConsolidationCalculator.totalDebtBalance', {
                    defaultValue: 'Total debt balance',
                  })}
                </th>
                <td>{calcResults.debtBalance}</td>
              </tr>
              <tr role="row">
                <th scope="row">
                  {t('PFDebtConsolidationCalculator.totalMonthlyPayments', {
                    defaultValue: 'Total monthly payments',
                  })}
                </th>
                <td>{calcResults.debtPayment}</td>
              </tr>
              <tr role="row">
                <th scope="row">
                  {t('PFDebtConsolidationCalculator.totalRemainingInterest', {
                    defaultValue: 'Total remaining interest to be paid',
                  })}
                </th>
                <td>{calcResults.debtInterest}</td>
              </tr>
              <tr role="row">
                <th scope="row">
                  {t('PFDebtConsolidationCalculator.totalNumberPaymentsRemainingInterest', {
                    defaultValue: 'Total number of payments remaining: (longest loan)',
                  })}
                </th>
                <td>{calcResults.debtTerm}</td>
              </tr>
              <tr role="row">
                <th className="section underline">
                  {t('PFDebtConsolidationCalculator.consolidatedLoanAnalysis', {
                    defaultValue: 'Consolidated loan analysis',
                  })}
                </th>
                <th className="hiddenHeader underline">
                  {t('PFDebtConsolidationCalculator.value', {
                    defaultValue: 'Value',
                  })}
                </th>
              </tr>
              <tr role="row">
                <th scope="row">
                  {t('PFDebtConsolidationCalculator.proposedLoanAmount', {
                    defaultValue: 'Proposed loan amount',
                  })}
                </th>
                <td>{calcResults.consBalance}</td>
              </tr>
              <tr role="row">
                <th scope="row">
                  {t('PFDebtConsolidationCalculator.requiredMonthlyPayment', {
                    defaultValue: 'Required monthly payment',
                  })}
                </th>
                <td>{calcResults.consPayment}</td>
              </tr>
              <tr role="row">
                <th scope="row">
                  {t('PFDebtConsolidationCalculator.totalRemainingInterest', {
                    defaultValue: 'Total remaining interest to be paid',
                  })}
                </th>
                <td>{calcResults.consInterest}</td>
              </tr>
              <tr role="row">
                <th scope="row">
                  {t('PFDebtConsolidationCalculator.totalNumberOfpaymentsRemainingFull', {
                    defaultValue: 'Total number of payments remaining: (full term)',
                  })}
                </th>
                <td>{calcResults.consTerm}</td>
              </tr>
              <tr role="row">
                <th className="section underline">
                  {t('PFDebtConsolidationCalculator.ApplyMonthlySavingsToLoan', {
                    defaultValue: 'If you apply your monthly savings to the new loan',
                  })}
                </th>
                <th className="hiddenHeader underline">
                  {t('PFDebtConsolidationCalculator.value', {
                    defaultValue: 'Value',
                  })}
                </th>
              </tr>
              <tr role="row">
                <th scope="row">
                  {t('PFDebtConsolidationCalculator.MonthlySavingsAmount', {
                    defaultValue: 'Monthly savings amount',
                  })}
                </th>
                <td>{calcResults.recommendSavings}</td>
              </tr>
              <tr role="row">
                <th scope="row">
                  {t('PFDebtConsolidationCalculator.totalTemainingInterestTobePaid', {
                    defaultValue: 'Total remaining interest to be paid',
                  })}
                </th>
                <td>{calcResults.recommendInterest}</td>
              </tr>
              <tr role="row">
                <th scope="row">
                  {t('PFDebtConsolidationCalculator.totalNumberPaymentsRemaining', {
                    defaultValue: 'Total number of payments remaining',
                  })}
                </th>
                <td>{calcResults.recommendTerm}</td>
              </tr>
            </tbody>
          </table>

          <Heading className={styles.ResultsHeading} element="h3" size={3}>
            {t('PFDebtConsolidationCalculator.graph', {
              defaultValue: 'Graph',
            })}
          </Heading>
          <img
            src={calcResults.chartUrl}
            alt={t('PFDebtConsolidationCalculator.graphOfResults', {
              defaultValue: 'Graph of results',
            })}
          />
        </div>
      )}
    </div>
  )
}

export { PFDebtConsolidationCalculator }
export default PFDebtConsolidationCalculator
